import {Box, Container, Header, Tabs} from "@amzn/awsui-components-react-v3";
import {AjvError, IChangeEvent} from "@rjsf/core";
import Form from "@rjsf/core";
import {JSONSchema7} from "json-schema";
import React from "react";
import {TabsProps} from "@amzn/awsui-components-react-v3/polaris/tabs/interfaces";
import {sortPrimaryArrayBySecondaryArrayOrder} from "src/utils/array-helper";
import {ThemeProvider} from "@material-ui/core";
import {startScreenInputFormStyles} from "src/styles/start-screen-inputform-styles";
import {hasJsonKey} from "src/utils/string-helper";

export const WORKFLOW_EXECUTION_SHARED_DATA = "workflowExecutionSharedData";

interface TabInputFormProps {
    stageInstanceInputSchemaMap: Record<string, string>;
    formInputData: { [key: string]: any };
    enableLiveValidate?: boolean;
    onTabFormChange?: <T>(formInput: IChangeEvent<T>, tabId: string) => void;
    onTabFormSubmit?: (tabId: string, fieldIndex: number) => void;
    onTabFormError?: (err: AjvError[], tabId: string) => void;
    stageInstanceUISchemaMap?: Record<string, string>;
    stageRenderingOrder?: string[];
    workflowInputSchema?: string;
    workflowInputData?: Record<string, any>;
}

interface WorkflowInputFormProps {
    workflowInputSchema?: string;
    workflowInputData?: Record<string, any>;
    enableLiveValidate?: boolean;
    onTabFormChange?: <T>(formInput: IChangeEvent<T>, tabId: string) => void;
}

const WorkflowInputForm = ({
    workflowInputSchema,
    workflowInputData,
    enableLiveValidate,
    onTabFormChange
}: WorkflowInputFormProps) => {
    if (!workflowInputSchema || !hasJsonKey(workflowInputSchema!)) return null;
    
    return (
        <Container header={<Header variant={"h3"}>Workflow Input Schema</Header>}>
            <ThemeProvider theme={startScreenInputFormStyles}>
                <Form
                    schema={JSON.parse(workflowInputSchema!) as JSONSchema7}
                    id={"workflow-level-input-form"}
                    data-testid={"workflow-level-input-form"}
                    omitExtraData={true}
                    liveOmit={true}
                    noHtml5Validate={true}
                    liveValidate={enableLiveValidate || false}
                    onChange={(formInput) => onTabFormChange && onTabFormChange(formInput, WORKFLOW_EXECUTION_SHARED_DATA)}
                    formData={workflowInputData}
                    showErrorList={false}
                    readonly={false}
                >
                    <button type="submit" style={{display: "none"}}>Submit</button>
                </Form>
            </ThemeProvider>
        </Container>
    );
};

const StageInputForm = React.forwardRef((props: {
    stageInstanceInputSchemaMap: Record<string, string>;
    stageInstanceUISchemaMap?: Record<string, string>;
    formInputData: { [key: string]: any };
    stageRenderingOrder?: string[];
    enableLiveValidate?: boolean;
    onTabFormChange?: <T>(formInput: IChangeEvent<T>, tabId: string) => void;
    onTabFormSubmit?: (tabId: string, fieldIndex: number) => void;
    onTabFormError?: (err: AjvError[], tabId: string) => void;
    hasWorkflowInput: boolean;
}, ref: any) => {
    const {
        stageInstanceInputSchemaMap,
        stageInstanceUISchemaMap,
        formInputData,
        stageRenderingOrder,
        enableLiveValidate,
        onTabFormChange,
        onTabFormSubmit,
        onTabFormError,
        hasWorkflowInput
    } = props;

    const tabOrder = React.useMemo(() =>
        sortPrimaryArrayBySecondaryArrayOrder(Object.keys(stageInstanceInputSchemaMap), stageRenderingOrder),
        [stageInstanceInputSchemaMap, stageRenderingOrder]);

    const schemaTabs: Array<TabsProps.Tab> = React.useMemo(() => {
        return tabOrder.map((tabId) => {
            const schema = stageInstanceInputSchemaMap[tabId] ?? JSON.stringify({});
            const stageUISchema = stageInstanceUISchemaMap && stageInstanceUISchemaMap[tabId] ? JSON.parse(stageInstanceUISchemaMap[tabId]) : {};
            return {
                label: tabId,
                id: tabId,
                content:
                    <ThemeProvider theme={startScreenInputFormStyles}>
                        <Form schema={JSON.parse(schema) as JSONSchema7}
                              id={"workflow-input-rjsf-form"}
                              data-testid={"workflow-input-rjsf-form"}
                              omitExtraData={true}
                              liveOmit={true}
                              noHtml5Validate={true}
                              liveValidate={enableLiveValidate || false}
                              onChange={(formInput) => onTabFormChange && onTabFormChange(formInput, tabId)}
                              formData={formInputData[tabId]}
                              showErrorList={false}
                              uiSchema={stageUISchema}
                              readonly={false}
                        >
                            <button type="submit" style={{display: "none"}}>Submit</button>
                        </Form>
                    </ThemeProvider>
            }
        })
    }, [stageInstanceInputSchemaMap, enableLiveValidate, formInputData, stageInstanceUISchemaMap, tabOrder, onTabFormChange]);

    const stageInputForms = React.useMemo(() => (
        <Box display={"none"}>
            {Object.entries(stageInstanceInputSchemaMap || {}).map(([tabId, schema], tabIndex) => (
                <Form schema={JSON.parse(schema) as JSONSchema7}
                      key={tabIndex}
                      omitExtraData={true}
                      liveOmit={true}
                      noHtml5Validate={true}
                      liveValidate={true}
                      onChange={(formInput) => onTabFormChange && onTabFormChange(formInput, tabId)}
                      formData={formInputData[tabId]}
                      showErrorList={false}
                      onSubmit={() => onTabFormSubmit && onTabFormSubmit(tabId, tabIndex)}
                      onError={(err: AjvError[]) => onTabFormError && onTabFormError(err, tabId)}
                >
                    <button key={tabIndex} id={`${tabId}Tab`} data-testid={`${tabId}Tab`} ref={el => {
                        if (ref?.current) {
                            ref.current[tabIndex] = el
                        }
                    }} type="submit">Submit</button>
                </Form>
            ))}
        </Box>
    ), [stageInstanceInputSchemaMap, formInputData, onTabFormChange, onTabFormSubmit, onTabFormError, ref]);

    return (
        <Container header={hasWorkflowInput ? <Header variant={"h3"}>Stage Input</Header> : undefined}>
            <Tabs tabs={schemaTabs} />
            {stageInputForms}
        </Container>
    );
});

export const TabInputForm = React.forwardRef((props: TabInputFormProps, ref: any) => {
    const {
        stageInstanceInputSchemaMap,
        formInputData,
        enableLiveValidate,
        onTabFormChange,
        onTabFormSubmit,
        onTabFormError,
        stageInstanceUISchemaMap,
        stageRenderingOrder,
        workflowInputSchema,
        workflowInputData
    } = props;

    return (
        <>
            <WorkflowInputForm
                workflowInputSchema={workflowInputSchema || ''}
                workflowInputData={workflowInputData}
                enableLiveValidate={enableLiveValidate}
                onTabFormChange={onTabFormChange}
            />
            <StageInputForm
                ref={ref}
                stageInstanceInputSchemaMap={stageInstanceInputSchemaMap}
                stageInstanceUISchemaMap={stageInstanceUISchemaMap}
                formInputData={formInputData}
                stageRenderingOrder={stageRenderingOrder}
                enableLiveValidate={enableLiveValidate}
                onTabFormChange={onTabFormChange}
                onTabFormSubmit={onTabFormSubmit}
                onTabFormError={onTabFormError}
                hasWorkflowInput={hasJsonKey(workflowInputSchema || '')}
            />
        </>
    )
})